import React, { useEffect, useRef, useState } from "react";
import { IMessage } from "@endpoints/types/message.type";
import LoadingAnimation from "@assets/svg/LoadingAnimation";
import useChat from "../../../custom-hooks/useChat";
import { identity } from "@utils/mergeMessages";
import MessageBubble from "./MessageBubble";
import { ISelectedContact } from "../MessagesPage";
import MessageInputBar from "./MessageInputBar";
import { useSelector } from "react-redux";
import { RootState } from "@store/reducers";

interface ChatListProps {
  selectedContact: ISelectedContact;
}

const ChatList = React.memo(({ selectedContact }: ChatListProps) => {
  const isInitialLoad = useRef(false);
  const messagesContainerRef = useRef(null);
  const [replyTarget, setReplyTarget] = useState<IMessage | null>(null);

  const { user } = useSelector((state: RootState) => state.auth);

  const {
    canSendMessage,
    messagingRestrictionReason,
    refreshMessagingPermission,
    isMessageLoading,
    messages,
    loadOlderMessages,
    sendMessage,
    appendLocalMessage,
    triggerTyping,
    isTyping,
    isOlderMessageLoading,
    hasNextPage,
  } = useChat({
    jobId: selectedContact?.jobId,
    receiverDetails: selectedContact?.participantDetails,
  });

  const handleScroll = () => {
    const el = messagesContainerRef.current;
    if (!el || isOlderMessageLoading) return;
    const isAtTop = el.scrollTop === 0;
    if (isAtTop && hasNextPage) {
      loadOlderMessages();
    }
  };

  const scrollToBottom = (smooth = false) => {
    if (messagesContainerRef?.current) {
      messagesContainerRef?.current.scrollTo({
        top: messagesContainerRef?.current?.scrollHeight,
        behavior: smooth ? "smooth" : "auto",
      });
    }
  };

  useEffect(() => {
    if (!messages?.length) return;

    const el = messagesContainerRef.current;
    if (!el) return;

    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const isNearBottom = distanceFromBottom < 150;
    // 20 is fetch message limit
    if (isNearBottom || isInitialLoad?.current) {
      isInitialLoad.current = false;
      scrollToBottom();
    }
  }, [messages]);

  useEffect(() => {
    if (selectedContact?.jobId) isInitialLoad.current = true;
  }, [selectedContact?.jobId, selectedContact?.participantDetails?.userId]);

  // Drop any pending reply target when switching conversations
  useEffect(() => {
    setReplyTarget(null);
  }, [selectedContact?.jobId, selectedContact?.participantDetails?.userId]);

  if (!selectedContact || isMessageLoading) {
    return (
      <div className="flex justify-center mt-40">
        <LoadingAnimation color="black" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full w-full relative px-5 pt-2 pb-8 min-h-0">
      <div className="bg-[#fafafa] rounded-md flex flex-col flex-1 min-h-0">
        <div
          ref={messagesContainerRef}
          onScroll={handleScroll}
          className="flex flex-col gap-5 flex-grow overflow-y-auto no-scrollbar p-6 min-h-0 h-[69vh]"
        >
          {isOlderMessageLoading ? (
            <div className="flex items-center justify-center font-medium text-xs gap-3">
              Loading older messages <LoadingAnimation color="#2e2e2e" />
            </div>
          ) : null}
          {messages?.length ? (
            messages?.map((item) => {
              return (
                <div
                  key={identity(item)}
                  className="flex flex-col gap-2"
                >
                  {/* {item?._id} {unseenMessageFrom?._id} */}
                  {/* {item?._id === unseenMessageFrom?._id ? (
                    <NewMessageDivider />
                  ) : null} */}
                  <MessageBubble
                    item={item}
                    user={user}
                    receiverDetails={selectedContact?.participantDetails}
                    onReply={setReplyTarget}
                  />
                </div>
              );
            })
          ) : (
            <p className="text-sm text-center flex h-[67vh] items-center justify-center pb-10">
              No messages available.
            </p>
          )}
        </div>

        {/* Typing and message Input area */}
        {!canSendMessage && <div role="status" className="p-3 text-xs text-gray-600">
          {messagingRestrictionReason}
          <button type="button" className="ml-2 underline" onClick={refreshMessagingPermission}>Check again</button>
        </div>}
        <MessageInputBar
          disabled={!canSendMessage}
          user={user}
          isTyping={isTyping}
          selectedContact={selectedContact}
          sendMessage={sendMessage}
          appendLocalMessage={appendLocalMessage}
          triggerTyping={triggerTyping}
          replyTarget={replyTarget}
          onCancelReply={() => setReplyTarget(null)}
        />
      </div>
    </div>
  );
});

export default ChatList;
